import React, { useState } from 'react'
import {
  Modal,
  Upload,
  Button,
  message,
  Alert,
  Typography,
  Space,
  Divider
} from 'antd'
import {
  DownloadOutlined,
  InboxOutlined
} from '@ant-design/icons'
import type { UploadProps } from 'antd'

const { Dragger } = Upload
const { Text } = Typography

interface PackingImportModalProps {
  visible: boolean
  onCancel: () => void
  onImport: (file: File) => Promise<void>
}

const PackingImportModal: React.FC<PackingImportModalProps> = ({
  visible,
  onCancel,
  onImport
}) => {
  const [uploading, setUploading] = useState(false)
  const [fileList, setFileList] = useState<any[]>([])

  const handleImport = async () => {
    if (fileList.length === 0) {
      message.error('请选择要导入的文件')
      return
    }

    const file = fileList[0].originFileObj
    setUploading(true)
    
    try {
      await onImport(file)
      setFileList([])
    } catch (error) {
      console.error('Import error:', error)
    } finally {
      setUploading(false)
    }
  }

  const handleCancel = () => {
    setFileList([])
    onCancel()
  }

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/packing/template/download', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error('下载模板失败')
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = '装箱清单导入模板.xlsx'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      message.success('模板下载成功')
    } catch (error) {
      console.error('Download template error:', error)
      message.error('下载模板失败')
    }
  }

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    fileList,
    beforeUpload: (file) => {
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                     file.type === 'application/vnd.ms-excel'
      
      if (!isExcel) {
        message.error('只能上传Excel文件！')
        return false
      }

      const isLt10M = file.size / 1024 / 1024 < 10
      if (!isLt10M) {
        message.error('文件大小不能超过10MB！')
        return false
      }

      setFileList([{
        uid: file.uid,
        name: file.name,
        status: 'done',
        originFileObj: file
      }])
      
      return false // 阻止自动上传
    },
    onRemove: () => {
      setFileList([])
    }
  }

  return (
    <Modal
      title="导入装箱规划"
      open={visible}
      onCancel={handleCancel}
      width={600}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          取消
        </Button>,
        <Button
          key="import"
          type="primary"
          loading={uploading}
          onClick={handleImport}
          disabled={fileList.length === 0}
        >
          开始导入
        </Button>
      ]}
    >
      <div>
        <Alert
          message="导入说明"
          description={
            <div>
              <p>1. 请使用标准的装箱清单导入模板</p>
              <p>2. 确保SKU信息已存在于系统中</p>
              <p>3. 同一箱子内不能包含不同颜色码的SKU</p>
              <p>4. 支持的文件格式：.xlsx, .xls</p>
              <p>5. 文件大小限制：10MB</p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Text strong>第一步：下载模板</Text>
            <div style={{ marginTop: 8 }}>
              <Button
                icon={<DownloadOutlined />}
                onClick={downloadTemplate}
              >
                下载装箱清单导入模板
              </Button>
            </div>
          </div>

          <Divider />

          <div>
            <Text strong>第二步：上传文件</Text>
            <div style={{ marginTop: 8 }}>
              <Dragger {...uploadProps}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
                <p className="ant-upload-hint">
                  支持单个文件上传，仅支持 .xlsx 和 .xls 格式
                </p>
              </Dragger>
            </div>
          </div>
        </Space>
      </div>
    </Modal>
  )
}

export default PackingImportModal